import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import SentimentTagEmoji from '@/images/sentiment/SentimentTagEmoji.svg';
import TrashLight from '@/images/TrashLight.svg';
import { ControlIconButton } from '@/pages/AdvancedEditor/components/panels/common/ControlIconButton';
import theme from '@/theme';
import { VideoType } from '@d-id/types';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { AvatarNewTag } from './AvatarNewTag';
import { AvatarTypeTag } from './AvatarTypeTag';
import { UpgradeBanner } from './UpgradeBanner';

export type Variant = 'default' | 'loading' | 'border' | 'lite-border';

function getColor(theme: Theme, variant: Variant = 'default', selected: boolean = false) {
    if (variant === 'border' || variant === 'lite-border') {
        if (selected) {
            return theme.colors.primary.main;
        }

        return 'transparent';
    }

    return !selected ? theme.colors.black[900] : theme.colors.black[800];
}

const getPadding = (variant?: Variant) => {
    if (variant === 'border') {
        return '4px';
    }

    if (variant === 'lite-border') {
        return '2px';
    }

    return '0';
};

const RemoveButton = styled(ControlIconButton)`
    width: 24px;
    height: 24px;
    border-radius: 4px !important;
`;

const Container = styled.div<{
    variant?: Variant;
    selected?: boolean;
    disabled: boolean;
    showRemove: boolean;
    type: string;
}>`
    position: relative;
    border-radius: 8px;
    background: ${({ theme, selected, variant }) => getColor(theme, variant, selected)};
    padding: ${({ variant }) => getPadding(variant)};
    font-family: inherit;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    ${RemoveButton} {
        display: none;
    }

    :hover {
        background: ${({ theme, variant, type, selected }) => (type === VideoType.CLIP ? getColor(theme, variant, selected) : null)};

        ${RemoveButton} {
            display: ${({ showRemove }) => (showRemove ? 'flex' : 'none')};
        }
    }

    > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
    }
`;

const LooksTag = styled.span`
    display: inline-flex;

    color: ${theme.colors.secondary.main};
    font-family: Manrope;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 12px;
    white-space: nowrap;

    margin-left: auto;
    padding: 3px 6px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    border: 1px solid ${theme.colors.black[300]};
    background: #fff;
`;

const Header = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    display: flex;
    align-items: top;
    width: 100%;
    padding: 8px;
    gap: 8px;
`;

const Footer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    border-radius: 0 0 8px 8px;
    width: 100%;
    padding: inherit;

    > div {
        display: flex;
        gap: 8px;
        padding: 8px;
    }
`;

const SentimentsTag = styled.div`
    display: inline-flex;
    height: 20px;
    padding: 0px 4px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    border-radius: 4px;
    background: rgba(9, 6, 4, 0.85);

    backdrop-filter: blur(8px);

    color: ${theme.colors.white};
    font-family: Manrope;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 10.118px;
`;

export interface IAssetCardProps<T> {
    children: React.ReactNode;
    variant?: Variant;
    premium?: boolean;
    selected?: boolean;
    disabled?: boolean;
    presenterId?: string;
    testId?: string;
    onClick?: (item: T) => void;
    onMouseOver?: () => void;
    onMouseOut?: () => void;
    onRemove?: (item: T) => Promise<void>;
    className?: string;
    item: T;
    type?: string;
    isNew?: boolean;
    isAiGenerated?: boolean;
    showExpressiveTag?: boolean;
    showUpgradeBanner?: boolean;
    progressColor?: 'primary' | 'secondary';
    looks?: number;
    sentiments?: number;
    headerElements?: React.ReactNode;
}

export function AssetCard<T>({
    children,
    variant = 'default',
    onClick,
    onRemove,
    onMouseOver,
    onMouseOut,
    className,
    item,
    selected,
    presenterId,
    disabled,
    type = '',
    testId = 'asset-card',
    isNew = false,
    showUpgradeBanner,
    progressColor = 'primary',
    isAiGenerated,
    showExpressiveTag = false,
    looks,
    sentiments,
    headerElements,
}: IAssetCardProps<T>) {
    const handleRemove = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        onRemove?.(item);
    };

    return (
        <Container
            showRemove={!!onRemove && variant !== 'loading'}
            data-testid={testId}
            type={type}
            className={className}
            variant={variant}
            selected={selected}
            disabled={!!disabled}
            onClick={() => !disabled && onClick?.(item)}
            onMouseOver={() => onMouseOver?.()}
            onMouseOut={() => onMouseOut?.()}>
            {variant === 'loading' ? <CircularProgress color={progressColor} size="24px" /> : children}

            <Header>
                <AvatarNewTag isNew={isNew} />
                <span style={{ marginLeft: 'auto' }} />
                {headerElements}
                <RemoveButton ariaLabel="remove" onClick={handleRemove}>
                    <MaskedIcon src={TrashLight} size="16px" color={theme.colors.black[900]} />
                </RemoveButton>
            </Header>

            <Footer>
                <div>
                    <AvatarTypeTag avatarId={presenterId} type={type} isAiGenerated={isAiGenerated} showExpressiveTag={showExpressiveTag} />
                    {!!looks && looks > 1 && <LooksTag>{looks} Looks</LooksTag>}
                    {!!sentiments && sentiments > 1 && (
                        <SentimentsTag>
                            <img src={SentimentTagEmoji} alt="Sentiment Tag Emoji" />
                            {sentiments}
                        </SentimentsTag>
                    )}
                </div>
                <UpgradeBanner show={showUpgradeBanner} />
            </Footer>
        </Container>
    );
}
